import { Variable } from '../../../app-shared-elements/_interfaces/Variable';
import { ObjectTypeEnum } from '../../_enums/object-type.enum';
import { VariableTypeOptionsEnum } from '../../_enums/variable-type-options.enum';
import { EMPTY_CHART_CONFIG } from './mnemonic-chart.mapper';
import { EMPTY_GAUGE_CONFIG } from './mnemonic-gauge.mapper';

export function isBoundMnemonicObject(object?: { variableId?: string } | null): boolean {
    return Boolean(object?.variableId);
}

export function shouldPersistBoundObjectName(name: string | undefined, objectType?: string): boolean {
    const normalizedName = name?.trim() ?? '';

    if (!normalizedName) {
        return false;
    }

    if (objectType === ObjectTypeEnum.chart && /^chart\d+$/i.test(normalizedName)) {
        return true;
    }

    if (objectType === ObjectTypeEnum.gauge && /^gauge\d+$/i.test(normalizedName)) {
        return true;
    }

    return false;
}

export function stripGaugeConfigForSave(gauge?: { min?: number; max?: number } | null): Record<string, number> {
    return {
        min: gauge?.min ?? EMPTY_GAUGE_CONFIG.min!,
        max: gauge?.max ?? EMPTY_GAUGE_CONFIG.max!,
    };
}

export function stripChartConfigForSave(chart?: { type?: string } | null): Record<string, unknown> {
    return {
        type: chart?.type ?? EMPTY_CHART_CONFIG.type ?? 'line',
        data: {
            labels: [],
            datasets: [{ data: [] }],
        },
    };
}

export function sanitizeBoundObjectForSave(item: Record<string, unknown>, currentObj?: any): Record<string, unknown> {
    if (!isBoundMnemonicObject(currentObj ?? item)) {
        return item;
    }

    const result = { ...item };
    const objectType = String(currentObj?.objectType ?? item.objectType ?? '');

    delete result.tooltip;

    if (!shouldPersistBoundObjectName(String(result.name ?? ''), objectType)) {
        delete result.name;
    }

    if (objectType === ObjectTypeEnum.text) {
        result.text = '';
    }

    if (objectType === ObjectTypeEnum.gauge) {
        result.gauge = stripGaugeConfigForSave(currentObj?.gauge ?? (result.gauge as { min?: number; max?: number }));
    }

    if (objectType === ObjectTypeEnum.chart) {
        result.chart = stripChartConfigForSave(currentObj?.chart ?? (result.chart as { type?: string }));
    }

    return result;
}

export function normalizeBoundObjectForLoad(object: Record<string, unknown>): Record<string, unknown> {
    if (!isBoundMnemonicObject(object)) {
        return object;
    }

    const result = { ...object };
    const objectType = String(result.objectType ?? '');
    const fabricType = String(result.type ?? '');

    if (
        objectType === ObjectTypeEnum.text ||
        fabricType === 'Text' ||
        fabricType === 'IText' ||
        fabricType === 'i-text'
    ) {
        if (result.text == null) {
            result.text = '';
        }
    }

    if (objectType === ObjectTypeEnum.chart && !result.chart) {
        result.chart = stripChartConfigForSave();
    }

    if (objectType === ObjectTypeEnum.gauge && !result.gauge) {
        result.gauge = stripGaugeConfigForSave();
    }

    return result;
}

export function formatBoundTextDisplay(
    variableName: string,
    currentVariable: Pick<Variable, 'currentValue' | 'unitName'>,
    selectOption?: VariableTypeOptionsEnum,
): string {
    const unit = currentVariable.unitName;
    const value = currentVariable.currentValue;

    switch (selectOption) {
        case VariableTypeOptionsEnum.all:
            return variableName + ' ' + (unit ? `${value}${unit}` : String(value ?? ''));
        case VariableTypeOptionsEnum.name:
            return variableName;
        case VariableTypeOptionsEnum.value:
            return unit ? `${value}${unit}` : String(value ?? '');
        default:
            return variableName;
    }
}

export function applyMnemonicBindingRuntime(
    obj: any,
    params: {
        variableName: string;
        bindingLabel: string;
        currentVariable: Pick<Variable, 'currentValue' | 'unitName' | 'status'>;
        selectOption?: VariableTypeOptionsEnum;
    },
): void {
    const { variableName, bindingLabel, currentVariable, selectOption } = params;

    if (obj.objectType === ObjectTypeEnum.gauge) {
        const value = Number(currentVariable.currentValue);
        obj.set('gauge', {
            ...(obj.gauge ?? {}),
            min: obj.gauge?.min ?? EMPTY_GAUGE_CONFIG.min,
            max: obj.gauge?.max ?? EMPTY_GAUGE_CONFIG.max,
            value: Number.isFinite(value) ? value : 0,
            unit: currentVariable.unitName ?? '',
            name: bindingLabel,
        });
        obj.set('name', bindingLabel);
        return;
    }

    if (obj.objectType === ObjectTypeEnum.chart) {
        const chart = obj.chart ?? { ...EMPTY_CHART_CONFIG };
        const datasets = chart.data?.datasets?.length ? [...chart.data.datasets] : [{ data: [] as number[] }];
        datasets[0] = {
            ...(datasets[0] ?? {}),
            label: bindingLabel,
        };

        obj.set({
            name: bindingLabel,
            chart: {
                ...chart,
                type: chart.type ?? 'line',
                data: {
                    labels: chart.data?.labels ?? [],
                    datasets,
                },
            },
        });
        return;
    }

    if (obj.objectType === ObjectTypeEnum.text || obj.objectType === ObjectTypeEnum.iText) {
        const text = formatBoundTextDisplay(variableName, currentVariable, selectOption);

        if (typeof obj.set === 'function') {
            obj.set('text', text);
            obj.set('name', bindingLabel);
        } else {
            obj.text = text;
        }

        obj.initDimensions?.();
        obj.setCoords?.();
        obj.dirty = true;
        return;
    }

    if (obj.objectType === ObjectTypeEnum.path) {
        obj.set('name', bindingLabel);
    }
}
